import moment from 'moment';
import { pluralize } from '@aha-app/aha-shared';

export const DAY = 86400;
export const HOUR = 3600;
export const MINUTE = 60;
export const MILLIS = 1000;
export const SECOND = 1;

export enum DurationUnit {
  Seconds = 'second',
  Minutes = 'minute',
  Hours = 'hour',
  Days = 'day',
}

export const durations = {
  [DurationUnit.Seconds]: {
    factor: SECOND,
    shortName: 'sec.',
  },
  [DurationUnit.Minutes]: {
    factor: MINUTE,
    shortName: 'min.',
  },
  [DurationUnit.Hours]: {
    factor: HOUR,
    shortName: 'hr.',
  },
  [DurationUnit.Days]: {
    factor: DAY,
    shortName: 'day',
  },
} as const;

export const MAX_TIME = 99 * MINUTE + 59 * SECOND;
export const EXACT_MAX_TIME = MAX_TIME * MILLIS;
export const MIN_TIME = SECOND;

export const DEFAULT_DURATION = 15 * MINUTE;

export function timeInSeconds(accurateTime: number): number {
  return Math.ceil(accurateTime / MILLIS);
}

export function timeInMillis(time: number): number {
  return time * MILLIS;
}

// Neither moment nor intl support the date format we want. So we'll
// format it directly.
export function formattedTimestamp(
  timestamp: number | null,
  { time = true }: { time?: boolean } = {}
): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);

  const months: string[] = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];

  const day: number = date.getDate();
  const monthIndex: number = date.getMonth();
  const year: number = date.getFullYear();
  const hours: number = date.getHours();
  const minutes: number = date.getMinutes();
  const period: string = hours < 12 ? 'a.m.' : 'p.m.';

  let formattedHours = hours;
  if (hours === 0) {
    formattedHours = 12;
  } else if (hours > 12) {
    formattedHours = hours - 12;
  }

  const dateString = `${months[monthIndex]} ${day}, ${year}`;
  const timeString = `${formattedHours}:${minutes
    .toString()
    .padStart(2, '0')} ${period}`;

  if (time) {
    return `${dateString} — ${timeString}`;
  } else {
    return dateString;
  }
}

// Returns details for displaying a time duration that changes
// resolution based on the unit. For example:
//
//   0:15 min (15 seconds)
//   1:30 min (1.5 minutes)
//   2:00 hr  (2 hours)
//   3 days   (3 days)
//
// In the return value:
//   - string is the value part.
//   - shortUnit is the suffix for display.
//   - unit is the detected DurationUnit, in case you need it for lookups.
export function shortFormattedTime(time: number): {
  string: string;
  unit: DurationUnit;
  shortUnit: string;
} {
  if (time <= 0)
    return {
      string: '0:00',
      unit: DurationUnit.Minutes,
      shortUnit: durations[DurationUnit.Minutes].shortName,
    };

  if (time >= DAY) {
    const days = Math.floor(time / DAY);
    return {
      string: days.toString(),
      unit: DurationUnit.Days,
      shortUnit: pluralize(days, DurationUnit.Days),
    };
  }

  if (time >= HOUR) {
    const hours = Math.floor(time / HOUR);
    const minutes = Math.floor((time % HOUR) / MINUTE);
    return {
      string: `${hours}:${minutes < 10 ? '0' : ''}${minutes}`,
      unit: DurationUnit.Hours,
      shortUnit: durations[DurationUnit.Hours].shortName,
    };
  }

  const minutes = Math.floor(time / MINUTE);
  const seconds = time % MINUTE;
  return {
    string: `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`,
    unit: DurationUnit.Minutes,
    shortUnit: durations[DurationUnit.Minutes].shortName,
  };
}

export function formattedTime(time: number): string {
  if (time <= 0) return '00:00';

  const minutes = Math.floor(time / MINUTE).toString();
  const seconds = time % MINUTE;
  let paddedSeconds;

  if (seconds > 9) {
    paddedSeconds = seconds.toString();
  } else {
    paddedSeconds = '0' + seconds;
  }

  return [minutes, paddedSeconds].join(':');
}

export function elapsedTime(start: number) {
  return Date.now() - start;
}

export function formatTime(time: string) {
  return moment(time).format('LT');
}

export function formatDate(time: string) {
  return moment(time).format('ll');
}
